import { useLayoutEffect, useRef } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { plantTagline } from '../../domain/plants/plantCatalogue';
import { findCategory } from '../../domain/plants/plantCategories';
import { DECISION_PROPERTIES } from '../../domain/plants/plantProperties';
import { CATEGORY_ICONS } from './CategoryIcons';
import { PROPERTY_ICONS } from './PlantIcons';
import { propertyShortValue } from './plantPropertyText';

/**
 * Who this plant is, on paper, under its picture (SPEC-024).
 *
 * The name, the botanical name, what kind of plant it is and the one line that
 * says what it is like used to hang over the bottom-left of the artwork, behind
 * a scrim drawn on the screen whose entire point is the artwork (D-2). Here it
 * is type on paper, and the picture above it is just the picture.
 *
 * The four glances are the bottom of this card rather than a band of their own:
 * a gardener reads "Silver Birch, a tree, 15–25 m" as one thought, and a
 * separate strip would have put a border through the middle of it.
 *
 * They are the SHORT vocabulary — "Medium", "Sun or shade", "Any soil" — which
 * is what makes four of them fit a phone's width where the full values need a
 * line each. The full value and the sentence that explains it are one scroll
 * down, in `DecisionPlate`, and this row never repeats them (SPEC-024 D-3).
 *
 * The handle at the top of the paper is SPEC-025 D-7. The mockups draw a pill
 * there and nothing drew one before; it arrives as a button rather than as
 * decoration, because lifting the card open is otherwise reachable only by a
 * drag — which a keyboard cannot perform and a screen reader cannot describe
 * (NFR-04) — and a handle is the one piece of this composition that already
 * looks pressable to a reader who never discovers the gesture.
 */

function Glance({ plant, property }) {
    const { t } = useLocale();
    const PropertyIcon = PROPERTY_ICONS[property];

    return (
        <li className={`glance glance--${property}`}>
            <PropertyIcon size={20} />
            {/* The short label — "Care", not "Maintenance". Four columns share a
                phone's width with no note under them to borrow from. */}
            <p className="glance__name">{t(`plants.property.${property}.short`)}</p>
            <p className="glance__value">{propertyShortValue(t, plant, property)}</p>
        </li>
    );
}

/**
 * How far the title may be blown up before it leaves the card (SPEC-025 D-14).
 *
 * The name arrives oversized, over the picture, and settles onto its line. How
 * oversized has to be MEASURED: the card clips, and the widest name in the
 * catalogue — *Mexican Feather Grass* — already wraps to two lines and nearly
 * fills the measure at its resting size. A scale that flatters "Lavender" throws
 * that one off the edge.
 *
 * A transform does not reflow, so the answer is simply how much room the widest
 * line has left, capped: enough to read as a title on a short name, and quietly
 * less on a long one. `Range` rather than the element's own box — the element is
 * a full-width block, and what must not overflow is the TEXT.
 */
const TITLE_SCALE_CAP = 1.9;

function fitTitleScale(heading) {
    if (!heading) {
        return;
    }

    const range = document.createRange();

    range.selectNodeContents(heading);

    const line = range.getBoundingClientRect().width;
    const room = heading.getBoundingClientRect().width;

    range.detach?.();

    heading.style.setProperty(
        '--title-scale',
        line > 0 ? Math.min(TITLE_SCALE_CAP, Math.max(1, room / line)).toFixed(3) : '1',
    );
}

export default function PlantBrief({ plant, isExpanded, onToggle, bodyId }) {
    const { t } = useLocale();
    const commonName = plant.prose?.commonName ?? plant.id;
    const headingRef = useRef(null);

    // Before paint, so the title never shows one frame at the wrong size. The
    // card is keyed on the plant, so this runs once per arrival.
    useLayoutEffect(() => fitTitleScale(headingRef.current), [commonName]);
    const category = findCategory(plant.category);
    const CategoryIcon = CATEGORY_ICONS[plant.category];
    const tagline = plantTagline(plant.prose);

    return (
        <section className="plant-brief">
            {/* The handle is on EVERY seated card, not only the one in hand.
                Gated to the front card it MOUNTED at the swap — a control
                appearing at the top of the paper on a card that was already on
                screen, which is one layer being replaced by another with very
                nearly the same content (D-36). It is inert on a seat that is not
                in hand, so it is a drawn pill and nothing more. */}
            <button
                    type="button"
                    className="plant-brief__handle"
                    // The card's grab point, for BOTH axes (SPEC-025 D-17):
                    // sideways deals the next plant, up opens this one. Read by
                    // `isSidewaysClaimed`, which would otherwise turn a drag on
                    // a button away — this control is a gesture surface as well
                    // as a control, and only it can say so.
                    data-gesture="grab"
                    onClick={onToggle || undefined}
                    aria-expanded={Boolean(isExpanded)}
                    aria-controls={bodyId || undefined}
                    aria-label={t(isExpanded ? 'plants.detail.closeFull' : 'plants.detail.openFull')}
                >
                    {/* The pill the mockups draw. Decorative — the button around
                        it is 44px of hit area and carries the whole label. */}
                    <span className="plant-brief__grip" aria-hidden="true" />
            </button>
            <h1 className="plant-detail__name" ref={headingRef}>{commonName}</h1>
            {/* One line where there were two: the species and what kind of plant
                it is are the same thought, and every row this card gives back is
                a row the picture above it takes (SPEC-024 D-8). It wraps to two
                when a Hungarian category will not sit beside a long binomial. */}
            <div className="plant-brief__line">
                <p className="plant-detail__botanical">{plant.botanicalName}</p>
                {category ? (
                    <p className="plant-detail__category">
                        {CategoryIcon ? <CategoryIcon size={18} /> : null}
                        {t(category.messageKey)}
                    </p>
                ) : null}
            </div>
            {tagline ? <p className="plant-detail__tagline">{tagline}</p> : null}
            {/* Named, because four icons and four values are a list of facts
                about something and a reader who cannot see the layout has no
                other way to know what the four belong to. */}
            <ul className="plant-glance" aria-label={t('plants.detail.atAGlance')}>
                {DECISION_PROPERTIES.map((property) => (
                    <Glance key={property} plant={plant} property={property} />
                ))}
            </ul>
        </section>
    );
}
